import React, { createContext, useContext, useEffect, useState } from 'react';

const CartContext = createContext();
const CART_KEY = 'ms_cart';
const CART_OWNER_KEY = 'ms_cart_owner';

const readStoredJson = (key, fallback) => {
  try {
    const value = localStorage.getItem(key);
    return value ? JSON.parse(value) : fallback;
  } catch {
    return fallback;
  }
};

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState(() => readStoredJson(CART_KEY, []));
  const [cartOwner, setCartOwner] = useState(() => readStoredJson(CART_OWNER_KEY, null));

  useEffect(() => {
    localStorage.setItem(CART_KEY, JSON.stringify(cart));
  }, [cart]);

  useEffect(() => {
    if (cartOwner) localStorage.setItem(CART_OWNER_KEY, JSON.stringify(cartOwner));
    else localStorage.removeItem(CART_OWNER_KEY);
  }, [cartOwner]);

  const addToCart = (material, owner, quantity = 1, variant = null) => {
    const switchingShop = cartOwner && cartOwner._id !== owner._id;
    if (switchingShop) {
      if (!window.confirm('Adding items from a different shop will clear your current cart. Continue?')) return;
    }
    setCartOwner(owner);
    setCart(prev => {
      const currentCart = switchingShop ? [] : prev;
      const key = variant ? `${material._id}-${variant.size}` : material._id;
      const existing = currentCart.find(i => i.key === key);
      if (existing) return currentCart.map(i => i.key === key ? { ...i, quantity: i.quantity + quantity } : i);
      const price = variant ? variant.price : material.price;
      return [...currentCart, { key, material, owner, quantity, variant, price, name: material.name, category: material.category, unit: variant?.unit || material.unit }];
    });
  };

  const removeFromCart = (key) => setCart(prev => prev.filter(i => i.key !== key));

  const updateQuantity = (key, qty) => {
    if (qty <= 0) return removeFromCart(key);
    setCart(prev => prev.map(i => i.key === key ? { ...i, quantity: qty } : i));
  };

  const clearCart = () => {
    setCart([]);
    setCartOwner(null);
    localStorage.removeItem(CART_KEY);
    localStorage.removeItem(CART_OWNER_KEY);
  };

  const replaceCart = (items, owner) => {
    setCart(items || []);
    setCartOwner(owner || null);
  };

  const total = cart.reduce((s, i) => s + i.price * i.quantity, 0);
  const count = cart.reduce((s, i) => s + i.quantity, 0);

  return (
    <CartContext.Provider value={{ cart, cartOwner, addToCart, replaceCart, removeFromCart, updateQuantity, clearCart, total, count }}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => useContext(CartContext);
